
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import ResourceCard from '@/components/ResourceCard';
import { useResources } from '@/hooks/useResourceContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, PlusCircle, Pen, Check, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const CategoryPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { categories, getResourcesByCategory, isAdmin, updateCategory, getCategoryById, settings } = useResources();
  
  // Get the category
  const category = categoryId ? getCategoryById(categoryId) : undefined;
  
  // Get resources for this category
  const resources = categoryId ? getResourcesByCategory(categoryId) : [];
  
  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedCategory, setEditedCategory] = useState(category ? { ...category } : undefined);

  // Update edited category when category changes (for persistence between renders)
  useEffect(() => {
    if (category) {
      setEditedCategory({ ...category });
    }
  }, [category]);

  if (!category) {
    return (
      <Layout>
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-red-500">Category not found</h1>
          <Button 
            onClick={() => navigate('/')}
            className="mt-4"
          >
            Return to Home
          </Button>
        </div>
      </Layout>
    );
  }

  const handleEditClick = () => {
    setEditedCategory({ ...category });
    setIsEditing(true);
  };

  const handleSaveCategory = () => {
    if (editedCategory && categoryId) {
      updateCategory(categoryId, editedCategory);
      setIsEditing(false);
      toast.success("Category updated successfully");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedCategory({ ...category }); // Reset to original values
  };

  // Check if user can edit (either admin or developer mode)
  const canEdit = isAdmin || settings.developerMode;

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-brand-blue hover:text-brand-darkBlue mr-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input 
                  value={editedCategory?.name || ''}
                  onChange={(e) => setEditedCategory({ ...editedCategory!, name: e.target.value })}
                  className="text-3xl font-bold"
                />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleSaveCategory}
                  className="text-green-500"
                >
                  <Check className="h-5 w-5" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleCancelEdit}
                  className="text-red-500"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center">
                <h1 className="text-3xl font-bold text-gray-800">{category.name}</h1>
                {canEdit && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleEditClick}
                    className="ml-2 text-gray-500 hover:text-brand-blue"
                  >
                    <Pen className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          {canEdit && (
            <Button 
              onClick={() => navigate('/add-resource', { state: { categoryId } })}
              className="bg-brand-blue hover:bg-brand-darkBlue text-white"
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Add Resource
            </Button>
          )}
        </div>

        {isEditing ? (
          <Textarea 
            value={editedCategory?.description || ''}
            onChange={(e) => setEditedCategory({ ...editedCategory!, description: e.target.value })}
            className="mb-6"
          />
        ) : (
          <p className="text-gray-600 mb-6">{category.description}</p>
        )}

        {resources.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-500">No resources found in this category.</p>
            {canEdit && (
              <Button 
                onClick={() => navigate('/add-resource', { state: { categoryId } })}
                className="mt-4 bg-brand-blue hover:bg-brand-darkBlue text-white"
              >
                <PlusCircle className="mr-2 h-4 w-4" /> Add your first resource
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map(resource => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CategoryPage;
